import {isArray} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {hasProperty} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {NumberInput} from '../../../../../libs/shared/ui/src/components/inputs/numberInput';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {NumberParameter} from '../../../editors/commons/parameterModel';
import {filterAllowedExpressions} from '../../../editors/expressions/expressionInferHelpers';
import {getLabelForTemplatePart} from '../../../editors/expressions/expressionLabels';
import {Expression} from '../../../editors/expressions/expressionModel';
import {ExpressionRef, isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {ResultTypesEnum} from '../../../editors/expressions/infer/expressionResultModel';
import {ExpressionPill} from './expressionPill';
import {RulesGenericInput, StyledInput} from './genericInput';
import {StringTemplateParameter} from './stringTemplate/stringTemplateParameter';

/*
 * Props.
 */

interface NumberTemplate {
  template: [ExpressionRef];
}

type NumberOrTemplate = NumberTemplate | number;

interface InputNumberProps {
  parameter: NumberParameter;
  value: NumberOrTemplate | undefined;
  isDisabled?: boolean;
  onValueChange: HandlerOf<NumberOrTemplate | ''>;
  expressions: ReadonlyArray<Expression>;
  onSelectCreateExpression?: Handler;
}

/*
 * Styles.
 */

const StyledExpressionWrapper = styled.div`
  flex: 1;
`;

const StyledIcon = styled(Icon)`
  margin-top: 3px;
`;

/*
 * Component.
 */

export const InputNumber: FC<InputNumberProps> = (props) => {
  const theme = useUserTheme();
  const {value, isDisabled, onValueChange, expressions, onSelectCreateExpression} = props;
  const {hasExpressions} = useBaseEditorContext();

  const validExpressions = filterAllowedExpressions(expressions, undefined, false, ResultTypesEnum.NUMBER);
  const shouldDisplayButton =
    !isDisabled && hasExpressions && (onSelectCreateExpression || validExpressions.length > 0);

  return (
    <RulesGenericInput inputId="number-input">
      {renderValue(value, expressions, onValueChange, isDisabled ?? false)}

      {shouldDisplayButton && (
        <StringTemplateParameter
          expressions={validExpressions}
          selectedItem={null}
          onPartSelected={(part) => isExpressionRef(part) && onValueChange({template: [part]})}
          renderButton={() => <StyledIcon name="plusCircleFilledSmall" color={theme.palette.blue.shade40} />}
          onSelectCreateExpression={onSelectCreateExpression}
        />
      )}
    </RulesGenericInput>
  );
};

/*
 * Helpers.
 */

export function isStringTemplate(value: unknown): value is NumberTemplate {
  return Boolean(
    hasProperty(value, 'template') && isArray(value.template) && value.template.every(isExpressionRef),
  );
}

function renderValue(
  value: number | NumberTemplate | undefined,
  expressions: readonly Expression[],
  onValueChange: HandlerOf<NumberOrTemplate | ''>,
  isDisabled: boolean,
) {
  if (typeof value === 'number' || !value) {
    return (
      <StyledInput as={NumberInput} value={value ?? 0} onChange={onValueChange} isDisabled={isDisabled} />
    );
  }

  return (
    <StyledExpressionWrapper>
      <ExpressionPill
        type={{type: ResultTypesEnum.NUMBER}}
        label={getLabelForTemplatePart(value.template[0], expressions)}
      />
      {/* TODO: Add close button */}
    </StyledExpressionWrapper>
  );
}
